/**
 * VideoThoughtPad — the watch-page ThoughtPad, mirroring Dhyana's note UI to the
 * tee (note cards with expand/collapse, type badges, indexing dot, images,
 * concept tags, delete; an input with snip + image upload).
 *
 * Differences from Dhyana, all additive:
 *   - video-origin notes carry note_metadata.timestamp_sec → a "▶ m:ss" chip
 *     that seeks the player;
 *   - new typed notes are stamped with the current playhead;
 *   - a graceful-block state when the lecture has no linked book (notes need a
 *     book + chapter to live on — see useVideoNotes / video_notes router).
 *
 * Owns only view state (input, expanded set, snip/zoom). The notes list + CRUD
 * are lifted to VideoWatch (via useVideoNotes) so a note pinned from the Chat tab
 * shows up here immediately.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PlayIcon,
  ScissorsIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { SnipModal } from './SnipModal';
import { ReadingNote, ReadingNoteCreate } from '../../types/reading-notes';

interface VideoThoughtPadProps {
  notes: ReadingNote[];
  loading: boolean;
  notLinked: boolean;
  error: string | null;
  fetchNotes: () => Promise<void>;
  createNote: (note: Omit<ReadingNoteCreate, 'book_id' | 'page_number'>) => Promise<ReadingNote | null>;
  deleteNote: (noteId: string) => Promise<void>;
  /** Seek the player when a "▶ m:ss" chip is clicked. */
  onSeek: (sec: number) => void;
  /** Current playhead (seconds) — stamped onto a new typed note. */
  getPlayhead: () => number;
  /** Capture the paused frame for snipping (absent → no snip button). */
  captureFrame?: () => Promise<string | null>;
}

interface PendingImage { data_url: string; file_name: string }

const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export const VideoThoughtPad: React.FC<VideoThoughtPadProps> = ({
  notes, loading, notLinked, error, fetchNotes, createNote, deleteNote, onSeek, getPlayhead, captureFrame,
}) => {
  const [input, setInput] = useState('');
  const [images, setImages] = useState<PendingImage[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [snipImage, setSnipImage] = useState<string | null>(null);
  const [snipError, setSnipError] = useState(false);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { void fetchNotes(); }, [fetchNotes]);

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    Array.from(e.target.files || []).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl) setImages((prev) => prev.length >= 5 ? prev : [...prev, { data_url: dataUrl, file_name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleSnip = async () => {
    if (!captureFrame) return;
    const frame = await captureFrame();
    if (!frame) { setSnipError(true); setTimeout(() => setSnipError(false), 4000); return; }
    setSnipImage(frame);
  };

  const handleSnipConfirm = (dataUrl: string) => {
    setSnipImage(null);
    const sec = getPlayhead();
    setImages((prev) => prev.length >= 5 ? prev : [...prev, { data_url: dataUrl, file_name: `frame-${fmtTime(sec)}.jpg` }]);
    inputRef.current?.focus();
  };

  const submit = async () => {
    const text = input.trim();
    if ((!text && images.length === 0) || saving) return;
    setSaving(true);
    try {
      await createNote({
        note_type: 'thought',
        user_note: text,
        image_urls: images.map((i) => i.data_url),
        note_metadata: { source: 'video', timestamp_sec: getPlayhead() },
      });
      setInput('');
      setImages([]);
    } catch { /* hook surfaces the error */ } finally {
      setSaving(false);
    }
  };

  // ── Graceful block: lecture not linked to a book ──
  if (notLinked) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center px-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-500/10">
          <SparklesIcon className="h-7 w-7 text-cyan-500 dark:text-[#06B6D4]" />
        </div>
        <p className="mt-4 font-semibold text-slate-800 dark:text-[#E2E8F0]">Notes are locked for this lecture</p>
        <p className="mt-1.5 max-w-xs text-sm text-slate-500 dark:text-slate-400">
          Your tutor needs to link this lecture to a book to unlock adding notes. Once linked, your notes here
          sync with that book&apos;s chapter in Dhyana.
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {/* Notes list */}
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-slim p-3">
        {notes.length === 0 && !loading ? (
          <div className="mt-8 text-center text-slate-500 dark:text-slate-400">
            <SparklesIcon className="mx-auto mb-4 h-12 w-12 opacity-20" />
            <p className="text-base font-medium">Your ThoughtPad</p>
            <p className="mt-1 text-sm">Jot a thought, snip the board, or pin an answer from Chat.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notes.map((note) => {
              const isOpen = expanded.has(note.id);
              const ts = note.note_metadata?.timestamp_sec;
              const hasSeek = note.note_metadata?.source === 'video' && typeof ts === 'number';
              return (
                <div
                  key={note.id}
                  className="cursor-pointer rounded-lg border-b border-slate-200 p-3 transition-colors last:border-0 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                  onClick={() => toggleExpanded(note.id)}
                >
                  <div className="mb-2 flex items-start justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      {isOpen ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
                      <span className="text-xs text-gray-500 dark:text-gray-400">{new Date(note.created_at).toLocaleString()}</span>
                      {note.note_type === 'highlight' && (
                        <span className="rounded bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">Highlight</span>
                      )}
                      {note.note_type === 'thought' && (
                        <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">Thought</span>
                      )}
                      {note.note_type === 'chat_answer' && (
                        <span className="rounded bg-cyan-100 px-2 py-0.5 text-xs text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200">AI Answer</span>
                      )}
                      {hasSeek && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onSeek(ts as number); }}
                          className="inline-flex items-center gap-1 rounded-md bg-cyan-500/10 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-cyan-600 transition hover:bg-cyan-500/20 dark:text-cyan-300"
                          title="Jump to this moment"
                        >
                          <PlayIcon className="h-2.5 w-2.5" />{fmtTime(ts as number)}
                        </button>
                      )}
                      {/* indexing status dot */}
                      {note.is_indexed === false && (
                        <span title={note.indexing_status === 'failed' ? `Indexing failed: ${note.indexing_error || ''}` : 'Indexing…'}>
                          {note.indexing_status === 'failed'
                            ? <span className="block h-2 w-2 rounded-full bg-red-500" />
                            : <span className="block h-2 w-2 animate-pulse rounded-full bg-yellow-500" />}
                        </span>
                      )}
                      {note.is_indexed === true && (
                        <span className="block h-2 w-2 rounded-full bg-green-500" title="Indexed for search" />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); void deleteNote(note.id); }}
                      className="rounded p-1 transition-colors hover:bg-red-100 dark:hover:bg-red-900/30"
                      title="Delete note"
                    >
                      <XMarkIcon className="h-4 w-4 text-red-500" />
                    </button>
                  </div>

                  {isOpen ? (
                    <>
                      {note.user_note && (
                        <div className="mb-2"><MarkdownRenderer content={note.user_note} className="text-sm font-medium" onTimestampClick={onSeek} /></div>
                      )}
                      {note.content_text && (
                        <div className="my-2 border-l-4 border-[#64748B] pl-4 dark:border-[#06B6D4]">
                          <MarkdownRenderer content={note.content_text} className="text-sm" onTimestampClick={onSeek} />
                        </div>
                      )}
                      {note.image_urls && note.image_urls.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {note.image_urls.map((url, idx) => (
                            <img key={idx} src={url} alt={`Note ${idx + 1}`} onClick={(e) => { e.stopPropagation(); setZoomedImage(url); }}
                              className="w-full cursor-zoom-in rounded-lg border border-gray-200 transition hover:opacity-90 dark:border-gray-700" />
                          ))}
                        </div>
                      )}
                      {note.concept_tags && note.concept_tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {note.concept_tags.map((tag, idx) => (
                            <span key={idx} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {(note.user_note || note.content_text) && (
                        <div className="mb-2 line-clamp-2"><MarkdownRenderer content={note.user_note || note.content_text || ''} className="text-sm" onTimestampClick={onSeek} /></div>
                      )}
                      {note.image_urls && note.image_urls.length > 0 && (
                        <div className="mt-2 flex gap-2">
                          {note.image_urls.slice(0, 2).map((url, idx) => (
                            <img key={idx} src={url} alt={`Note ${idx + 1}`} className="h-16 w-16 rounded-lg border border-gray-200 object-cover dark:border-gray-700" />
                          ))}
                          {note.image_urls.length > 2 && (
                            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 text-sm font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">+{note.image_urls.length - 2}</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-slate-200 p-3 dark:border-slate-700/50">
        {snipError && (
          <div className="mb-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            Frame capture didn&apos;t complete — allow the tab-share prompt and snip again (or upload a screenshot).
          </div>
        )}
        {error && (
          <div className="mb-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">{error}</div>
        )}
        {images.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {images.map((a, i) => (
              <div key={i} className="group relative">
                <img src={a.data_url} alt={a.file_name} title={a.file_name} onClick={() => setZoomedImage(a.data_url)}
                  className="h-16 w-16 cursor-zoom-in rounded-lg border border-slate-200 object-cover shadow-sm transition hover:opacity-90 dark:border-slate-700" />
                <button onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))} aria-label={`Remove ${a.file_name}`}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white opacity-0 shadow transition-opacity group-hover:opacity-100 dark:bg-slate-600">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
        <div className="relative">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void submit(); }
              else if (e.key === 'Escape') { e.preventDefault(); setInput(''); }
            }}
            rows={1}
            placeholder="Jot down your thoughts…  (⌘/Ctrl+Enter to save)"
            className="w-full resize-none overflow-y-auto rounded-xl border border-slate-200 bg-white p-3.5 pr-32 text-sm leading-relaxed text-slate-800 placeholder-slate-400 shadow-sm transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 dark:border-slate-700 dark:bg-slate-800/50 dark:text-[#E2E8F0] dark:placeholder-[#94A3B8] dark:focus:border-[#06B6D4] dark:focus:ring-[#06B6D4]/30"
            style={{ minHeight: '110px', maxHeight: '240px' }}
          />
          <div className="absolute right-3 top-3 flex items-center gap-2">
            {input.trim() && (
              <button onClick={() => { setInput(''); inputRef.current?.focus(); }} title="Clear (Esc)"
                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-500">
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
            {captureFrame && (
              <button onClick={handleSnip} disabled={images.length >= 5} title="Snip the paused frame into a note"
                className="rounded-lg p-2 text-cyan-600 transition-colors hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-[#06B6D4] dark:hover:bg-[#06B6D4]/10">
                <ScissorsIcon className="h-5 w-5" />
              </button>
            )}
            <button onClick={() => fileInputRef.current?.click()} disabled={images.length >= 5} title="Attach an image"
              className="rounded-lg p-2 text-cyan-600 transition-colors hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-[#06B6D4] dark:hover:bg-[#06B6D4]/10">
              <PaperClipIcon className="h-5 w-5" />
            </button>
            <button onClick={() => void submit()} disabled={(!input.trim() && images.length === 0) || saving} title="Save note (⌘/Ctrl+Enter)"
              className="rounded-lg p-2 text-white shadow-sm transition hover:opacity-90 hover:shadow-glow-cyan disabled:cursor-not-allowed disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg,#06B6D4,#3B82F6)' }}>
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {snipImage && <SnipModal imageUrl={snipImage} onConfirm={handleSnipConfirm} onClose={() => setSnipImage(null)} />}

      {zoomedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setZoomedImage(null)}>
          <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setZoomedImage(null)} className="absolute -top-12 right-0 flex items-center gap-2 text-white transition-colors hover:text-gray-300">
              <span className="text-sm">Close</span><XMarkIcon className="h-6 w-6" />
            </button>
            <img src={zoomedImage} alt="Note preview" className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoThoughtPad;
